package com.shadowings.gero.data.lessons

import com.shadowings.gero.data.repository.PossessivePairs
import com.shadowings.gero.data.repository.SentenceOptions
import com.shadowings.gero.domain.model.Option
import com.shadowings.gero.domain.model.SingleSelectionContent
import com.shadowings.gero.domain.model.WordSelectionContent
import com.shadowings.gero.presentation.model.Word
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.quiz_ripasso_quale_gero_significa
import kotlin.random.Random

// Noun + suffix pronoun generator (G2 in CAPITOLI_16_18_PIANO.md): pr.f "la sua casa". The suffix
// goes after the whole noun, determinative and plural strokes included. In the translation the
// possessive agrees with the thing owned, not with the owner.

data class PossessivePhrase(
    val hieroglyphs: String,
    val transliteration: String,
    val correctPhrase: String,
    val correctTokens: List<String>,
    val noun: Word,
    val pronoun: Word
)

val POSSESSIVE_PRONOUN_IDS = listOf("IO", "TU_M", "TU_F", "EGLI", "ELLA", "NOI", "VOI", "ESSI")


private fun isFeminineOwned(noun: Word, languageCode: String): Boolean = when (languageCode) {
    "it" -> noun.isFeminineIt
    "fr" -> noun.isFeminineFr
    "es" -> noun.getArticolo("es")?.trim() in setOf("la", "las")
    "de" -> noun.getArticolo("de")?.trim() == "die"
    else -> noun.isFeminine
}

// Masculine singular, feminine singular, masculine plural, feminine plural.
private fun formOf(forms: List<String>, feminine: Boolean, plural: Boolean): String =
    forms[(if (plural) 2 else 0) + (if (feminine) 1 else 0)]

private fun italianPossessive(pronounId: String, feminine: Boolean, plural: Boolean): String = when (pronounId) {
    "IO" -> formOf(listOf("mio", "mia", "miei", "mie"), feminine, plural)
    "TU_M", "TU_F" -> formOf(listOf("tuo", "tua", "tuoi", "tue"), feminine, plural)
    "EGLI", "ELLA" -> formOf(listOf("suo", "sua", "suoi", "sue"), feminine, plural)
    "NOI" -> formOf(listOf("nostro", "nostra", "nostri", "nostre"), feminine, plural)
    "VOI" -> formOf(listOf("vostro", "vostra", "vostri", "vostre"), feminine, plural)
    else -> "loro"
}

private fun spanishPossessive(pronounId: String, feminine: Boolean, plural: Boolean): String = when (pronounId) {
    "IO" -> if (plural) "mis" else "mi"
    "TU_M", "TU_F" -> if (plural) "tus" else "tu"
    "NOI" -> formOf(listOf("nuestro", "nuestra", "nuestros", "nuestras"), feminine, plural)
    "VOI" -> formOf(listOf("vuestro", "vuestra", "vuestros", "vuestras"), feminine, plural)
    else -> if (plural) "sus" else "su"
}

// "ma" becomes "mon" before a vowel: "mon âme".
private fun frenchPossessive(pronounId: String, feminine: Boolean, plural: Boolean, nounText: String): String {
    val feminineForm = feminine && !startsWithVowelOrH(nounText)
    return when (pronounId) {
        "IO" -> if (plural) "mes" else if (feminineForm) "ma" else "mon"
        "TU_M", "TU_F" -> if (plural) "tes" else if (feminineForm) "ta" else "ton"
        "EGLI", "ELLA" -> if (plural) "ses" else if (feminineForm) "sa" else "son"
        "NOI" -> if (plural) "nos" else "notre"
        "VOI" -> if (plural) "vos" else "votre"
        else -> if (plural) "leurs" else "leur"
    }
}

private fun startsWithVowelOrH(word: String): Boolean =
    word.lowercase().firstOrNull()?.let { it in "aeiouàâéèêìîòôùûh" } == true

// Nominative: -e with a feminine or plural noun ("meine Frau", "unsere Häuser"). The accusative
// differs only with a masculine singular noun: -en ("seinen Sohn", "euren Stier").
private fun germanPossessive(pronounId: String, feminineOrPlural: Boolean, masculineAccusative: Boolean = false): String {
    val stem = when (pronounId) {
        "IO" -> "mein"
        "TU_M", "TU_F" -> "dein"
        "EGLI" -> "sein"
        "NOI" -> "unser"
        "VOI" -> "euer"
        else -> "ihr"
    }
    val ending = when {
        masculineAccusative -> "en"
        feminineOrPlural -> "e"
        else -> return stem
    }
    return if (stem == "euer") "eur$ending" else stem + ending
}

private fun englishPossessive(pronounId: String): String = when (pronounId) {
    "IO" -> "my"
    "EGLI" -> "his"
    "ELLA" -> "her"
    "NOI" -> "our"
    "ESSI" -> "their"
    else -> "your"
}

// The possessive alone, agreeing with a noun of the given gender and number.
private fun possessiveWord(pronounId: String, languageCode: String, feminine: Boolean, plural: Boolean, nounText: String): String =
    when (languageCode) {
        "it" -> italianPossessive(pronounId, feminine, plural)
        "es" -> spanishPossessive(pronounId, feminine, plural)
        "fr" -> frenchPossessive(pronounId, feminine, plural, nounText)
        "de" -> germanPossessive(pronounId, feminine || plural)
        else -> englishPossessive(pronounId)
    }

// German accusative -en and genitive -es/-er forms ("seinen", "deines", "unserer").
private fun germanCaseForms(pronounId: String): List<String> {
    val stem = germanPossessive(pronounId, false).let { if (it == "euer") "eur" else it }
    return listOf(germanPossessive(pronounId, false, masculineAccusative = true), "${stem}es", "${stem}er")
}

// Every form of every possessive in a language, for wrong tiles ("mio", "mia", "miei", "mie", "tuo"...).
fun possessiveForms(languageCode: String): List<String> =
    POSSESSIVE_PRONOUN_IDS.flatMap { id ->
        listOf(false, true).flatMap { feminine ->
            listOf(false, true).map { plural -> possessiveWord(id, languageCode, feminine, plural, "") }
        } + if (languageCode == "de") germanCaseForms(id) else emptyList()
    }.distinct()

// asObject: the noun is a direct object. Only German (accusative: "seinen Sohn") and Spanish
// (personal "a": "a su padre") change.
fun possessiveTranslation(noun: Word, pronounId: String, languageCode: String, asObject: Boolean = false, animalIsThing: Boolean = false): String {
    val nounText = getCleanMeaning(noun, languageCode)
    val plural = noun.isPlural
    val feminine = isFeminineOwned(noun, languageCode)
    val germanAccusative = asObject && languageCode == "de" && !plural && noun.getArticolo("de")?.trim() == "der"
    if (germanAccusative) {
        return "${germanPossessive(pronounId, false, masculineAccusative = true)} ${germanObliqueSingular(noun, nounText)}"
    }
    val possessive = possessiveWord(pronounId, languageCode, feminine, plural, nounText)
    // Italian drops the article with a singular relative (words.tsv it_possessive_no_article:
    // "mio padre"), but not with loro ("il loro padre").
    val italianArticle = languageCode == "it" && !(noun.itPossessiveNoArticle && pronounId != "ESSI")
    val phrase = if (italianArticle) "${articleFor(feminine, plural)} $possessive $nounText" else "$possessive $nounText"
    return if (asObject && languageCode == "es" && needsSpanishPersonalA(noun, animalIsThing)) "a $phrase" else phrase
}

// The owner of a noun with its own suffix possessive, as a genitive after it: "di tuo padre",
// "del loro padre", "of your father", "deines Vaters", "de tu padre", "de ton père".
fun possessiveGenitiveTranslation(owner: Word, pronounId: String, languageCode: String): String {
    val owned = possessiveTranslation(owner, pronounId, languageCode)
    return when (languageCode) {
        "it" -> {
            val article = owned.substringBefore(" ")
            when (article) {
                "il" -> "del ${owned.substringAfter(" ")}"
                "la" -> "della ${owned.substringAfter(" ")}"
                "i" -> "dei ${owned.substringAfter(" ")}"
                "le" -> "delle ${owned.substringAfter(" ")}"
                else -> "di $owned"
            }
        }
        // Genitive: -es with a masculine or neuter singular noun, which takes -(e)s itself
        // ("deines Vaters", "unseres Soldaten"), -er with a feminine or plural one ("meiner Mutter").
        "de" -> {
            val nounText = getCleanMeaning(owner, "de")
            val feminineOrPlural = owner.isPlural || owner.getArticolo("de")?.trim() == "die"
            val stem = germanPossessive(pronounId, false)
            val possessive = (if (stem == "euer") "eur" else stem) + if (feminineOrPlural) "er" else "es"
            val noun = if (feminineOrPlural) nounText else formatGermanGenitivePhrase("des", nounText, owner).removePrefix("des ")
            "$possessive $noun"
        }
        "en" -> "of $owned"
        else -> "de $owned"
    }
}

// Every possessive starts with a consonant that takes il/i (mio, tuo, suo, nostro, vostro, loro).
private fun articleFor(feminine: Boolean, plural: Boolean): String = when {
    feminine && plural -> "le"
    feminine -> "la"
    plural -> "i"
    else -> "il"
}

fun buildPossessivePhrase(noun: Word, pronoun: Word, languageCode: String): PossessivePhrase {
    val phrase = possessiveTranslation(noun, pronoun.id, languageCode)
    return PossessivePhrase(
        hieroglyphs = "${noun.gardiner}-${pronoun.gardiner}",
        transliteration = "${noun.reading}.${pronoun.reading}",
        correctPhrase = phrase,
        correctTokens = tokenizePhrase(phrase),
        noun = noun,
        pronoun = pronoun
    )
}

// A noun can take a suffix possessive if possessives.tsv lists it with a pronoun among the given words.
fun canTakePossessive(nounId: String, words: List<Word>, pairs: List<PossessivePairs>): Boolean =
    pairs.any { row -> nounId in row.nouns && row.pronouns.any { id -> words.any { it.id == id } } }

// The same sentence rows with only the subjects that can take a possessive, for the builders'
// subjectHavePossessive: rows left without subjects are dropped.
fun withPossessableSubjects(
    rows: List<SentenceOptions>,
    words: List<Word>,
    pairs: List<PossessivePairs>
): List<SentenceOptions> = rows
    .map { row -> row.copy(subjects = row.subjects.filter { canTakePossessive(it, words, pairs) }) }
    .filter { it.subjects.isNotEmpty() }

// Same for objectHavePossessive: only the objects that can take a possessive.
fun withPossessableObjects(
    rows: List<SentenceOptions>,
    words: List<Word>,
    pairs: List<PossessivePairs>
): List<SentenceOptions> = rows
    .map { row -> row.copy(objects = row.objects.filter { canTakePossessive(it, words, pairs) }) }
    .filter { it.objects.isNotEmpty() }

// Same for objectPossessorHavePossessive: only the owners of the object (possessore dell'oggetto)
// that can take a possessive themselves.
fun withPossessablePossessors(
    rows: List<SentenceOptions>,
    words: List<Word>,
    pairs: List<PossessivePairs>
): List<SentenceOptions> = rows
    .map { row -> row.copy(possessors = row.possessors.filter { id -> words.any { it.id == id } && canTakePossessive(id, words, pairs) }) }
    .filter { it.possessors.isNotEmpty() && it.objects.isNotEmpty() }

// The suffix pronoun for a noun: a random pronoun among those possessives.tsv allows for it, or
// null if it can't take one (the sentence then goes without).
fun pickPossessivePronoun(
    noun: Word,
    words: List<Word>,
    pairs: List<PossessivePairs>,
    random: Random
): Word? {
    val pronounIds = pairs.filter { noun.id in it.nouns }.flatMap { it.pronouns }.distinct()
    val pronouns = pronounIds.mapNotNull { id -> words.firstOrNull { it.id == id } }
    return pronouns.randomOrNull(random)
}

// Every valid pair in possessives.tsv, once each, on words the player has already met.
fun calculatePossessivePhrases(
    words: List<Word>,
    pairs: List<PossessivePairs>,
    languageCode: String
): List<PossessivePhrase> {
    val wordMap = DistractorPool.words(words).associateBy { it.id }
    return pairs
        .flatMap { row -> row.nouns.flatMap { noun -> row.pronouns.map { noun to it } } }
        .distinct()
        .mapNotNull { (nounId, pronounId) ->
            val noun = wordMap[nounId] ?: return@mapNotNull null
            val pronoun = wordMap[pronounId] ?: return@mapNotNull null
            buildPossessivePhrase(noun, pronoun, languageCode)
        }
}

// Hieroglyphs → build the translation. The wrong tiles are the possessives of the other persons
// (and the same person in the other gender, to check the agreement), plus another noun.
fun buildPossessiveWordSelection(
    phrase: PossessivePhrase,
    otherNouns: List<Word>,
    languageCode: String,
    random: Random = Random
): WordSelectionContent {
    val nounText = getCleanMeaning(phrase.noun, languageCode)
    val feminine = isFeminineOwned(phrase.noun, languageCode)
    val plural = phrase.noun.isPlural
    val otherGender = possessiveWord(phrase.pronoun.id, languageCode, !feminine, plural, nounText)
    val otherPersons = POSSESSIVE_PRONOUN_IDS
        .filter { it != phrase.pronoun.id }
        .map { possessiveWord(it, languageCode, feminine, plural, nounText) }
        .distinct()
        .shuffled(random)
        .take(3)
    val otherNoun = otherNouns.filter { it.id != phrase.noun.id }.randomOrNull(random)
    val wrongTokens = listOf(otherGender) + otherPersons +
        listOfNotNull(otherNoun?.let { getCleanMeaning(it, languageCode) })

    // A distractor equal to a needed tile would swallow it.
    val tiles = (phrase.correctTokens + wrongTokens.filter { wrong ->
        phrase.correctTokens.none { it.trim().lowercase() == wrong.trim().lowercase() }
    }.distinct())
        .filter { it.isNotBlank() }
        .shuffled(random)

    return WordSelectionContent(
        hieroglyphs = phrase.hieroglyphs,
        correctPhrase = phrase.correctPhrase,
        words = tiles,
        transliteration = phrase.transliteration
    )
}

// Translation → pick the right hieroglyphs. Wrong options: another suffix, the suffix before the
// noun, and the direct genitive with a noun as the owner (pr sš). A suffix whose translation is
// the same (.f and .s are both "suo" in Italian, "your" is .k, .ṯ and .ṯn) is never a wrong option.
fun buildPossessiveFormChoice(
    phrase: PossessivePhrase,
    pronouns: List<Word>,
    owners: List<Word>,
    languageCode: String,
    random: Random = Random
): SingleSelectionContent {
    val noun = phrase.noun
    val otherSuffix = pronouns
        .filter { it.id != phrase.pronoun.id }
        .filter { possessiveTranslation(noun, it.id, languageCode) != phrase.correctPhrase }
        .shuffled(random)
    val owner = owners.filter { it.id != noun.id }.randomOrNull(random)

    val wrong = buildList {
        otherSuffix.firstOrNull()?.let { add("${noun.gardiner}-${it.gardiner}") }
        add("${phrase.pronoun.gardiner}-${noun.gardiner}")
        owner?.let { add("${noun.gardiner}-${it.gardiner}") }
        otherSuffix.drop(1).forEach { add("${noun.gardiner}-${it.gardiner}") }
    }.filter { it != phrase.hieroglyphs }.distinct().take(3)

    return SingleSelectionContent(
        text = Res.string.quiz_ripasso_quale_gero_significa,
        textArgs = listOf(phrase.correctPhrase),
        detail = "",
        options = (listOf(Option(phrase.hieroglyphs, correct = true)) + wrong.map { Option(it, correct = false) })
            .shuffled(random)
    )
}
